import { useState } from "react";
import { confirmPasswordReset } from "../lib/auth";
import { Button } from "../components/ui/Button";

const MIN_LEN = 8;

/** Read ?token= out of the hash (e.g. #/reset-password?token=abc). */
function tokenFromHash(): string {
  const hash = window.location.hash || "";
  const q = hash.indexOf("?");
  if (q === -1) return "";
  return new URLSearchParams(hash.slice(q + 1)).get("token") || "";
}

export default function ResetPassword() {
  const [token] = useState(tokenFromHash);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setError("This reset link is missing its token. Request a new one.");
      return;
    }
    if (password.length < MIN_LEN) {
      setError(`Password must be at least ${MIN_LEN} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const ok = await confirmPasswordReset(token, password);
      if (ok) {
        setDone(true);
        setTimeout(() => {
          window.location.hash = "#/login";
        }, 1500);
        return;
      }
      setError("This reset link is invalid or has expired. Request a new one.");
    } catch {
      setError("Could not reach the server. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm max-w-md mx-auto">
      <h2 className="text-xl font-semibold text-gray-900 mb-1">Choose a new password</h2>
      {done ? (
        <p className="text-sm text-gray-600 mt-2">
          Password updated. Redirecting you to sign in…
        </p>
      ) : (
        <>
          <p className="text-sm text-gray-600 mb-5">Enter and confirm your new password.</p>
          <form onSubmit={submit} className="space-y-3">
            <input
              type="password"
              autoFocus
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password"
              className="w-full text-sm border border-gray-200 rounded-md px-3 py-2"
            />
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password"
              className="w-full text-sm border border-gray-200 rounded-md px-3 py-2"
            />
            {error && <div className="text-sm text-red-600">{error}</div>}
            <Button type="submit" variant="primary" disabled={submitting} className="w-full">
              {submitting ? "Saving…" : "Set new password"}
            </Button>
          </form>
          <div className="mt-4 text-sm text-center">
            <a href="#/login" className="text-accent-700 hover:underline">
              Back to sign in
            </a>
          </div>
        </>
      )}
    </div>
  );
}
